import type { ReactNode } from "react";
import { Check, Lock, LockOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Closed until what it waits on holds, ready once it does, settled once the
 * attorney has acted; plain for a bar that opens no gate, only a way elsewhere.
 */
export type ActionBarState = "closed" | "ready" | "settled" | "plain";

/** The gate's own mark: shut, open, passed. */
const GLYPH: Record<Exclude<ActionBarState, "plain">, ReactNode> = {
  closed: <Lock />,
  ready: <LockOpen />,
  settled: <Check />,
};

const STICKY = "sticky bottom-4 z-20";

/** The bar's ground: the card's, the brand's wash where asked while closed, green once open. */
function barClasses({
  open,
  alone,
  sticky,
  wash,
}: {
  open: boolean;
  alone: boolean;
  sticky: boolean;
  wash: boolean;
}): string {
  return cn(
    "flex flex-wrap items-center gap-4 rounded-[13px] border border-border bg-card px-4 py-3.5 shadow-bar",
    alone && "mt-6",
    alone && sticky && STICKY,
    wash && !open && "bg-muted",
    open && "border-success-border bg-success-surface",
  );
}

/** The tinted square with the gate's mark, green once the way on is open. */
function GateMark({ state }: { state: Exclude<ActionBarState, "plain"> }) {
  return (
    <span
      aria-hidden
      className={cn(
        "grid size-9 flex-none place-items-center rounded-inset border [&_svg]:size-[19px]",
        state === "closed"
          ? "border-border bg-muted text-brand-mid"
          : "border-success-border bg-card text-success",
      )}
    >
      {GLYPH[state]}
    </span>
  );
}

/**
 * The bar at the foot of a page that gates the next one: a tinted square with
 * the gate's mark, a heading over one sentence, what is still outstanding under
 * it, and the one action that closes the page. The mark follows the state, so
 * a stage cannot draw an open lock on a shut bar.
 *
 * The design draws it the same on every stage, so the shape is here and each
 * stage supplies only its words. It has no second control: the only way past a
 * closed bar is to meet what it waits on.
 */
export function ActionBar({
  heading,
  detail,
  state,
  actionLabel,
  actionIcon,
  actionTrail,
  busy = false,
  busyLabel,
  disabled = state === "closed",
  onAction,
  sticky = false,
  wash = false,
  above,
  testId,
  children,
}: {
  heading: string;
  detail: string;
  state: ActionBarState;
  actionLabel: string;
  /** Drawn inside the action, before its words: a gate's mark. */
  actionIcon?: ReactNode;
  /** Drawn inside the action, after its words: the way on, as an arrow. */
  actionTrail?: ReactNode;
  /** Something the action waits on is on its way. */
  busy?: boolean;
  busyLabel?: string;
  /** Whether the action is closed, when that is not simply whether the state is. */
  disabled?: boolean;
  onAction: () => void;
  /** Kept in view at the foot of the content column. */
  sticky?: boolean;
  /** Closed, the bar takes the brand's wash, as v0.28 draws Intake's. */
  wash?: boolean;
  /** Drawn just above the bar and kept in view with it: a banner over the page's pending changes. */
  above?: ReactNode;
  testId: string;
  /** Under the sentence: a refusal, a warning, what is outstanding. */
  children?: ReactNode;
}) {
  // Ready and settled both read green: the way on is open.
  const open = state === "ready" || state === "settled";
  const alone = above === undefined || above === null || above === false;
  const bar = (
    <div
      data-testid={testId}
      data-state={state}
      // Read by what floats over the column's foot, so it can sit above the bar.
      data-pinned={sticky || undefined}
      className={barClasses({ open, alone, sticky, wash })}
    >
      {/* The square and the words sit closer together than the words and the
          action, and are wide enough to read before the action wraps under. */}
      <div className="flex min-w-0 flex-1 basis-[240px] items-center gap-[11px]">
        {state !== "plain" && <GateMark state={state} />}
        <div className="min-w-0 flex-1">
          <strong className="block text-[13.5px] font-extrabold text-foreground">
            {heading}
          </strong>
          <span className="text-[12px] leading-[1.45] text-muted-foreground">
            {detail}
          </span>
          {children}
        </div>
      </div>
      <Button
        type="button"
        onClick={onAction}
        disabled={disabled || busy}
        // The design's bar action: taller than a row's, and closed filled in
        // the line colour rather than faded, so its words stay readable.
        className="h-[42px] gap-2 rounded-inset px-[22px] text-[13.5px] font-bold [&_svg]:size-[17px] disabled:border-border disabled:bg-border disabled:text-subtle-foreground disabled:opacity-100"
      >
        {actionIcon}
        {busy && busyLabel !== undefined ? busyLabel : actionLabel}
        {actionTrail}
      </Button>
    </div>
  );
  if (alone) return bar;
  // The banner rides with the bar, so the one sticky container keeps both in view.
  return (
    <KeptAtFoot sticky={sticky}>
      {above}
      {bar}
    </KeptAtFoot>
  );
}

/**
 * The foot of the content column, kept in view as the page scrolls: the bar
 * with what rides above it, or a banner alone where a page has no bar.
 */
export function KeptAtFoot({
  sticky = true,
  children,
}: {
  sticky?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn("mt-6 flex flex-col gap-2.5", sticky && STICKY)}>
      {children}
    </div>
  );
}

/** Something outstanding, and where on the page it is, when the page can place it. */
export interface ActionBarJump {
  key: string;
  label: string;
  onJump?: () => void;
}

/**
 * What the bar is still waiting on, listed under its sentence in the same type,
 * each a jump to the thing it names. One the page cannot place is named and
 * goes nowhere.
 */
export function ActionBarJumps({ jumps }: { jumps: readonly ActionBarJump[] }) {
  if (jumps.length === 0) return null;
  return (
    <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
      {jumps.map((jump) => (
        <li key={jump.key}>
          <button
            type="button"
            onClick={jump.onJump}
            disabled={jump.onJump === undefined}
            className="cursor-pointer text-left text-[12px] font-semibold text-primary underline-offset-2 hover:underline disabled:cursor-default disabled:text-muted-foreground disabled:no-underline"
          >
            {jump.label}
          </button>
        </li>
      ))}
    </ul>
  );
}
